import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { addMonths, subMonths } from "date-fns";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../config/firebase";
import {
  FaCalendarAlt,
  FaListAlt,
  FaGlobe,
  FaBan,
  FaSignOutAlt,
  FaChartBar,
  FaClock,
  FaCheckCircle,
  FaFlag,
  FaTimesCircle,
  FaEuroSign,
  FaChevronLeft,
  FaChevronRight,
  FaSyncAlt,
  FaAnchor,
} from "react-icons/fa";
import { GiSpeedBoat, GiSailboat } from "react-icons/gi";
import { FaUmbrellaBeach } from "react-icons/fa";

import adminCalendarService from "../services/adminCalendarService";
import DayDetailPanel from "../components/admin/DayDetailPanel";
import AdminBookingsTable from "../components/admin/AdminBookingsTable";
import ExternalBookingsPanel from "../components/admin/ExternalBookingsPanel";
import RevenuePanel from "../components/admin/RevenuePanel";

/* ── Month-grid helpers ──────────────────────────────────────── */
const _pad = (n) => String(n).padStart(2, "0");
const _toDS = (y, m, d) => `${y}-${_pad(m + 1)}-${_pad(d)}`;
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function _isBeforeToday(y, m, d) {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return new Date(y, m, d) < t;
}
function _isToday(y, m, d) {
  const t = new Date();
  return t.getFullYear() === y && t.getMonth() === m && t.getDate() === d;
}
function _sameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/* Fill -> colour helpers (3-state: Open / Almost / Full) */
function _fillBg(fill, hasData) {
  if (!hasData || fill === 0)
    return {
      bar: "bg-emerald-500",
      pill: "bg-emerald-100 text-emerald-700",
      cellBorder: "border-l-emerald-400",
    };
  if (fill >= 1)
    return {
      bar: "bg-red-500",
      pill: "bg-red-100 text-red-700",
      cellBorder: "border-l-red-400",
    };
  return {
    bar: "bg-orange-500",
    pill: "bg-orange-100 text-orange-700",
    cellBorder: "border-l-orange-400",
  };
}
function _fillLabel(fill, hasData, bookedCapacity, totalCapacity) {
  if (!hasData || fill === 0) return "Open";
  if (fill >= 1) return `Full ${totalCapacity}p`;
  const available = totalCapacity - bookedCapacity;
  return `${bookedCapacity}/${available}p`;
}

function AdminMonthGrid({
  year,
  month,
  daySummaries,
  selectedDay,
  onSelectDay,
}) {
  const byDate = useMemo(() => {
    const m = {};
    daySummaries.forEach((s) => (m[s.date] = s));
    return m;
  }, [daySummaries]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstOffset; i++)
    cells.push({ empty: true, key: `e${i}` });
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = _toDS(year, month, d);
    const s = byDate[dateStr] || {};
    const past = _isBeforeToday(year, month, d);
    const today = _isToday(year, month, d);
    const fill =
      s.totalCapacity > 0 ? (s.bookedCapacity ?? 0) / s.totalCapacity : 0;
    cells.push({
      d,
      dateStr,
      s,
      fill,
      past,
      today,
      blocked: s.blocked,
      hasData: !!s.date && s.totalCapacity > 0,
      empty: false,
    });
  }
  const tail = cells.length % 7 === 0 ? 0 : 7 - (cells.length % 7);
  for (let i = 0; i < tail; i++) cells.push({ empty: true, key: `t${i}` });

  return (
    <div className="select-none">
      {/* Week-day header */}
      <div className="grid grid-cols-7 mb-1">
        {WEEK_DAYS.map((h) => (
          <div
            key={h}
            className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wide py-1.5"
          >
            {h}
          </div>
        ))}
      </div>

      {/* Grid with gap-px dividers */}
      <div className="grid grid-cols-7 gap-px bg-gray-100 rounded-xl overflow-hidden border border-gray-100">
        {cells.map((cell, idx) => {
          if (cell.empty)
            return (
              <div
                key={cell.key || idx}
                className="bg-white min-h-[44px] sm:min-h-[58px]"
              />
            );

          const { d, dateStr, fill, past, today, blocked, hasData, s } = cell;
          const isSel =
            selectedDay &&
            _sameDay(selectedDay, new Date(dateStr + "T12:00:00"));
          const { bar, pill, cellBorder } = _fillBg(fill, hasData);
          const label = _fillLabel(
            fill,
            hasData,
            s.bookedCapacity ?? 0,
            s.totalCapacity ?? 0,
          );

          /* past */
          if (past) {
            return (
              <div
                key={dateStr}
                className="bg-white min-h-[44px] sm:min-h-[58px] flex items-start justify-center pt-1.5"
              >
                <span className="text-[10px] sm:text-xs text-gray-300 font-medium">
                  {d}
                </span>
              </div>
            );
          }

          /* blocked */
          if (blocked) {
            return (
              <div
                key={dateStr}
                onClick={() => onSelectDay(new Date(dateStr + "T12:00:00"))}
                className={`bg-gray-50 hover:bg-gray-100 cursor-pointer min-h-[44px] sm:min-h-[58px] flex flex-col items-center justify-between pt-1.5 pb-1 border-l-2 border-l-gray-300 transition-colors${isSel ? " ring-1 ring-inset ring-sky-400" : ""}`}
              >
                <span
                  className={`text-[10px] sm:text-xs font-semibold ${today ? "text-cyan-600" : "text-gray-400"}`}
                >
                  {today ? (
                    <span className="inline-flex items-center justify-center w-[17px] h-[17px] rounded-full bg-cyan-500 text-white text-[8px] font-bold">
                      {d}
                    </span>
                  ) : (
                    d
                  )}
                </span>
                <span className="text-[9px] text-gray-400 pb-0.5">blocked</span>
              </div>
            );
          }

          /* normal day */
          return (
            <div
              key={dateStr}
              onClick={() => onSelectDay(new Date(dateStr + "T12:00:00"))}
              className={[
                "cursor-pointer min-h-[44px] sm:min-h-[58px] flex flex-col justify-between pt-1.5 pb-1 px-1 border-l-2 transition-colors duration-100",
                cellBorder,
                isSel
                  ? "bg-sky-50 ring-1 ring-inset ring-sky-400"
                  : today
                    ? "bg-cyan-50 hover:bg-cyan-100"
                    : "bg-white hover:bg-slate-50",
              ].join(" ")}
            >
              {/* Date */}
              <span
                className={[
                  "text-[10px] sm:text-xs font-semibold leading-none self-start",
                  isSel
                    ? "text-sky-700"
                    : today
                      ? "text-cyan-600"
                      : "text-gray-700",
                ].join(" ")}
              >
                {today ? (
                  <span className="inline-flex items-center justify-center w-[17px] h-[17px] sm:w-[19px] sm:h-[19px] rounded-full bg-cyan-500 text-white text-[8px] sm:text-[9px] font-bold">
                    {d}
                  </span>
                ) : (
                  d
                )}
              </span>

              {/* Pill label */}
              <span
                className={`self-start text-[8px] sm:text-[9px] font-semibold px-1 py-0.5 rounded-md leading-none ${isSel ? "bg-sky-100 text-sky-700" : pill}`}
              >
                {label}
              </span>

              {/* Fill bar */}
              {hasData && (
                <div className="w-full h-[3px] bg-gray-100 rounded-full overflow-hidden mt-0.5">
                  <div
                    className={`h-full rounded-full ${isSel ? "bg-sky-400" : bar}`}
                    style={{ width: `${Math.min(100, fill * 100)}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Simple admin password gate ───────────────────────────── */
/* Password is verified server-side — never stored in the browser bundle */

/* ── Stat card ─────────────────────────────────────────────── */
function StatCard({ label, value, icon: Icon, iconBg, iconColor }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}
      >
        <Icon className={`text-base ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold text-slate-900 leading-none truncate">
          {value}
        </p>
        <p className="text-xs text-slate-500 mt-0.5 truncate">{label}</p>
      </div>
    </div>
  );
}

/* ── Block-dates section ───────────────────────────────────── */
function BlockDatesSection({ onDataChange }) {
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [msg, setMsg] = useState(null);

  const notify = (text, type = "info") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const load = useCallback(async () => {
    const data = await adminCalendarService.getBlockedDates();
    setBlocked(data.sort((a, b) => a.date.localeCompare(b.date)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const buildRange = (start, end) => {
    const days = [];
    const s = new Date(start),
      e = new Date(end);
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1))
      days.push(d.toISOString().split("T")[0]);
    return days;
  };

  const handleBlock = async () => {
    if (!rangeStart) {
      notify("Choose a start date", "error");
      return;
    }
    const dates =
      rangeEnd && rangeEnd >= rangeStart
        ? buildRange(rangeStart, rangeEnd)
        : [rangeStart];
    setSaving(true);
    try {
      await adminCalendarService.blockDates(
        dates,
        reason || "Admin blocked",
        "all",
      );
      notify(`${dates.length} date(s) blocked`, "success");
      load();
      if (onDataChange) onDataChange();
      setRangeStart("");
      setRangeEnd("");
      setReason("");
    } catch (err) {
      notify("Error: " + err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUnblock = async (date, serviceId) => {
    await adminCalendarService.unblockDate(date, serviceId);
    notify("Unblocked", "success");
    load();
    if (onDataChange) onDataChange();
  };

  return (
    <div className="space-y-6">
      {msg && (
        <div
          className={`px-4 py-2 rounded-lg text-sm font-medium border
          ${
            msg.type === "success"
              ? "bg-green-50 text-green-800 border-green-300"
              : msg.type === "error"
                ? "bg-red-50   text-red-800   border-red-300"
                : "bg-blue-50 text-blue-800 border-blue-300"
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* Block form */}
      <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
        <h3 className="font-semibold text-red-800 mb-4">🚫 Block Date Range</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              className="border rounded-xl px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              End Date (optional)
            </label>
            <input
              type="date"
              value={rangeEnd}
              min={rangeStart}
              onChange={(e) => setRangeEnd(e.target.value)}
              className="border rounded-xl px-3 py-2 text-sm"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs text-gray-600 mb-1">
              Reason (optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Maintenance, Holiday…"
              className="w-full border rounded-xl px-3 py-2 text-sm"
            />
          </div>
          <button
            onClick={handleBlock}
            disabled={saving || !rangeStart}
            className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 transition"
          >
            Block
          </button>
        </div>
      </div>

      {/* Blocked list */}
      <div>
        <h3 className="font-semibold text-gray-700 mb-3">
          Currently Blocked Dates ({blocked.length})
        </h3>
        {blocked.length === 0 ? (
          <p className="text-gray-400 text-sm">No blocked dates.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {blocked.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between border border-red-200 bg-red-50 rounded-xl px-4 py-2.5"
              >
                <div>
                  <p className="font-semibold text-red-800 text-sm">{b.date}</p>
                  <p className="text-xs text-red-500">
                    {b.reason || "—"} · {b.serviceId}
                  </p>
                </div>
                <button
                  onClick={() => handleUnblock(b.date, b.serviceId)}
                  className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-lg hover:bg-green-200 font-medium"
                >
                  Unblock
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN ADMIN CALENDAR PAGE
   ══════════════════════════════════════════════════════════ */
export default function AdminCalendar() {
  /* ── Auth gate ─────────────────────────────────────────── */
  const [authed, setAuthed] = useState(
    () => sessionStorage.getItem("sp_admin") === "1",
  );
  const [passInput, setPassInput] = useState("");
  const [passErr, setPassErr] = useState(false);
  const [passLoading, setPassLoading] = useState(false);

  const handleLogin = async () => {
    if (!passInput.trim()) return;
    setPassLoading(true);
    setPassErr(false);
    try {
      const { httpsCallable } = await import("firebase/functions");
      const { functions } = await import("../config/firebase");
      const verify = httpsCallable(functions, "verifyAdminPassword");
      await verify({ password: passInput });
      sessionStorage.setItem("sp_admin", "1");
      setAuthed(true);
    } catch {
      setPassErr(true);
      setTimeout(() => setPassErr(false), 2000);
    } finally {
      setPassLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden">
          {/* Card top accent */}
          <div className="h-1.5 bg-gradient-to-r from-sky-400 via-cyan-400 to-teal-400" />
          <div className="p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <span className="text-3xl">🌊</span>
              </div>
              <h1 className="text-2xl font-bold text-slate-800">Admin Panel</h1>
              <p className="text-sm text-slate-400 mt-1">SeaPulse Albania</p>
            </div>
            <input
              type="password"
              placeholder="Enter admin password"
              value={passInput}
              onChange={(e) => setPassInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              className={`w-full border-2 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none transition
                ${passErr ? "border-red-400 bg-red-50" : "border-slate-200 focus:border-sky-400"}`}
            />
            {passErr && (
              <p className="text-red-500 text-xs mb-3 text-center font-medium">
                Incorrect password
              </p>
            )}
            <button
              onClick={handleLogin}
              disabled={passLoading}
              className="w-full bg-slate-900 text-white font-semibold py-3 rounded-xl hover:bg-slate-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {passLoading ? "Verifying…" : "Sign In"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <AdminDashboard />;
}

/* ── Inner dashboard (rendered after auth) ─────────────────── */
function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("calendar");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [daySummaries, setDaySummaries] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    cancelled: 0,
    completed: 0,
    revenue: 0,
  });
  const [calLoading, setCalLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null); // Date object

  // Durrës: Bayliner calendar
  const [bayCurrentDate, setBayCurrentDate] = useState(new Date());
  const [bayDaySummaries, setBayDaySummaries] = useState([]);
  const [bayCalLoading, setBayCalLoading] = useState(true);
  const [baySelectedDay, setBaySelectedDay] = useState(null);

  // Durrës: SpeedBoat Tour calendar
  const [sbCurrentDate, setSbCurrentDate] = useState(new Date());
  const [sbDaySummaries, setSbDaySummaries] = useState([]);
  const [sbCalLoading, setSbCalLoading] = useState(true);
  const [sbSelectedDay, setSbSelectedDay] = useState(null);

  // Luxury Yacht calendar
  const [yachtCurrentDate, setYachtCurrentDate] = useState(new Date());
  const [yachtDaySummaries, setYachtDaySummaries] = useState([]);
  const [yachtCalLoading, setYachtCalLoading] = useState(true);
  const [yachtSelectedDay, setYachtSelectedDay] = useState(null);

  // Secret Beach Escape calendar
  const [sbeCurrentDate, setSbeCurrentDate] = useState(new Date());
  const [sbeDaySummaries, setSbeDaySummaries] = useState([]);
  const [sbeCalLoading, setSbeCalLoading] = useState(true);
  const [sbeSelectedDay, setSbeSelectedDay] = useState(null);

  const loadCalendar = useCallback(async () => {
    setCalLoading(true);
    try {
      const y = currentDate.getFullYear();
      const m = currentDate.getMonth() + 1;
      const [summaries, allBookings] = await Promise.all([
        adminCalendarService.getMonthSummary(y, m),
        adminCalendarService.getAllBookings(),
      ]);
      setDaySummaries(summaries);

      // Global stats from all bookings
      const s = {
        total: allBookings.length,
        pending: 0,
        confirmed: 0,
        cancelled: 0,
        completed: 0,
        revenue: 0,
      };
      allBookings.forEach((b) => {
        if (b.status === "pending") s.pending++;
        if (b.status === "confirmed") s.confirmed++;
        if (b.status === "cancelled") s.cancelled++;
        if (b.status === "completed") s.completed++;
        if (b.status !== "cancelled") s.revenue += Number(b.totalPrice) || 0;
      });
      setStats(s);
    } catch (err) {
      console.error("Error loading calendar:", err);
    } finally {
      setCalLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  // Real-time listener: auto-refresh calendar when bookings, external bookings, or blocked dates change
  const reloadTimer = useRef(null);
  useEffect(() => {
    const pad = (n) => String(n).padStart(2, "0");
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth() + 1;
    const start = `${y}-${pad(m)}-01`;
    const last = new Date(y, m, 0).getDate();
    const end = `${y}-${pad(m)}-${pad(last)}`;

    // Each listener tracks whether its initial snapshot has been received.
    // Only changes AFTER the initial load trigger a reload.
    let extReady = false;
    let bkReady = false;
    let blockReady = false;

    const debouncedReload = () => {
      clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(() => loadCalendar(), 400);
    };

    // Listen to externalBookings collection-wide (no date filter) so revenue
    // stats update whenever ANY external booking is modified, e.g. extras added
    // by a customer on a past or future booking.
    const extQ = collection(db, "externalBookings");
    // Listen to bookings collection-wide — any booking update (ANY month,
    // including extras added via the Add Extras page) triggers a revenue reload.
    const bkQ = collection(db, "bookings");
    // Listen to blockedDates in this month so the grid refreshes when dates are blocked/unblocked
    const blockQ = query(
      collection(db, "blockedDates"),
      where("date", ">=", start),
      where("date", "<=", end),
    );

    const unsub1 = onSnapshot(extQ, () => {
      if (!extReady) {
        extReady = true;
        return;
      }
      debouncedReload();
    });
    const unsub2 = onSnapshot(bkQ, () => {
      if (!bkReady) {
        bkReady = true;
        return;
      }
      debouncedReload();
    });
    const unsub3 = onSnapshot(blockQ, () => {
      if (!blockReady) {
        blockReady = true;
        return;
      }
      debouncedReload();
    });

    return () => {
      unsub1();
      unsub2();
      unsub3();
      clearTimeout(reloadTimer.current);
    };
  }, [currentDate, loadCalendar]);

  // ── Durrës: SpeedBoat Tour calendar ─────────────────────────
  const loadSbCalendar = useCallback(async () => {
    setSbCalLoading(true);
    try {
      const y = sbCurrentDate.getFullYear();
      const m = sbCurrentDate.getMonth() + 1;
      const summaries = await adminCalendarService.getMonthSummary(
        y,
        m,
        "speedboat",
      );
      setSbDaySummaries(summaries);
    } catch (err) {
      console.error("Error loading speedboat calendar:", err);
    } finally {
      setSbCalLoading(false);
    }
  }, [sbCurrentDate]);

  useEffect(() => {
    loadSbCalendar();
  }, [loadSbCalendar]);

  const sbReloadTimer = useRef(null);
  useEffect(() => {
    let bkReady = false;
    let extReady = false;
    const debouncedSbReload = () => {
      clearTimeout(sbReloadTimer.current);
      sbReloadTimer.current = setTimeout(() => loadSbCalendar(), 400);
    };
    const sbBkQ = query(
      collection(db, "bookings"),
      where("serviceId", "==", "speedboat"),
    );
    const sbExtQ = query(
      collection(db, "externalBookings"),
      where("serviceId", "==", "speedboat"),
    );
    const unsub6 = onSnapshot(sbBkQ, () => {
      if (!bkReady) {
        bkReady = true;
        return;
      }
      debouncedSbReload();
    });
    const unsub7 = onSnapshot(sbExtQ, () => {
      if (!extReady) {
        extReady = true;
        return;
      }
      debouncedSbReload();
    });
    return () => {
      unsub6();
      unsub7();
      clearTimeout(sbReloadTimer.current);
    };
  }, [loadSbCalendar]);

  // ── Luxury Yacht calendar ────────────────────────────────────
  const loadYachtCalendar = useCallback(async () => {
    setYachtCalLoading(true);
    try {
      const y = yachtCurrentDate.getFullYear();
      const m = yachtCurrentDate.getMonth() + 1;
      const summaries = await adminCalendarService.getMonthSummary(
        y,
        m,
        "luxury-yacht",
      );
      setYachtDaySummaries(summaries);
    } catch (err) {
      console.error("Error loading yacht calendar:", err);
    } finally {
      setYachtCalLoading(false);
    }
  }, [yachtCurrentDate]);

  useEffect(() => {
    loadYachtCalendar();
  }, [loadYachtCalendar]);

  const yachtReloadTimer = useRef(null);
  useEffect(() => {
    let bkReady = false;
    let extReady = false;
    const debouncedYachtReload = () => {
      clearTimeout(yachtReloadTimer.current);
      yachtReloadTimer.current = setTimeout(() => loadYachtCalendar(), 400);
    };
    const yachtBkQ = query(
      collection(db, "bookings"),
      where("serviceId", "==", "luxury-yacht"),
    );
    const yachtExtQ = query(
      collection(db, "externalBookings"),
      where("serviceId", "==", "luxury-yacht"),
    );
    const unsubY1 = onSnapshot(yachtBkQ, () => {
      if (!bkReady) {
        bkReady = true;
        return;
      }
      debouncedYachtReload();
    });
    const unsubY2 = onSnapshot(yachtExtQ, () => {
      if (!extReady) {
        extReady = true;
        return;
      }
      debouncedYachtReload();
    });
    return () => {
      unsubY1();
      unsubY2();
      clearTimeout(yachtReloadTimer.current);
    };
  }, [loadYachtCalendar]);

  // ── Secret Beach Escape calendar ─────────────────────────────
  const loadSbeCalendar = useCallback(async () => {
    setSbeCalLoading(true);
    try {
      const y = sbeCurrentDate.getFullYear();
      const m = sbeCurrentDate.getMonth() + 1;
      const summaries = await adminCalendarService.getMonthSummary(
        y,
        m,
        "secret-beach-escape",
      );
      setSbeDaySummaries(summaries);
    } catch (err) {
      console.error("Error loading secret beach calendar:", err);
    } finally {
      setSbeCalLoading(false);
    }
  }, [sbeCurrentDate]);

  useEffect(() => {
    loadSbeCalendar();
  }, [loadSbeCalendar]);

  const sbeReloadTimer = useRef(null);
  useEffect(() => {
    let bkReady = false;
    let extReady = false;
    const debouncedSbeReload = () => {
      clearTimeout(sbeReloadTimer.current);
      sbeReloadTimer.current = setTimeout(() => loadSbeCalendar(), 400);
    };
    const sbeBkQ = query(
      collection(db, "bookings"),
      where("serviceId", "==", "secret-beach-escape"),
    );
    const sbeExtQ = query(
      collection(db, "externalBookings"),
      where("serviceId", "==", "secret-beach-escape"),
    );
    const unsubS1 = onSnapshot(sbeBkQ, () => {
      if (!bkReady) {
        bkReady = true;
        return;
      }
      debouncedSbeReload();
    });
    const unsubS2 = onSnapshot(sbeExtQ, () => {
      if (!extReady) {
        extReady = true;
        return;
      }
      debouncedSbeReload();
    });
    return () => {
      unsubS1();
      unsubS2();
      clearTimeout(sbeReloadTimer.current);
    };
  }, [loadSbeCalendar]);

  // ── Durrës: Bayliner calendar ────────────────────────────────
  const loadBayCalendar = useCallback(async () => {
    setBayCalLoading(true);
    try {
      const y = bayCurrentDate.getFullYear();
      const m = bayCurrentDate.getMonth() + 1;
      const summaries = await adminCalendarService.getMonthSummary(
        y,
        m,
        "bayliner",
      );
      setBayDaySummaries(summaries);
    } catch (err) {
      console.error("Error loading bayliner calendar:", err);
    } finally {
      setBayCalLoading(false);
    }
  }, [bayCurrentDate]);

  useEffect(() => {
    loadBayCalendar();
  }, [loadBayCalendar]);

  const bayReloadTimer = useRef(null);
  useEffect(() => {
    let bkReady = false;
    let extReady = false;
    const debouncedBayReload = () => {
      clearTimeout(bayReloadTimer.current);
      bayReloadTimer.current = setTimeout(() => loadBayCalendar(), 400);
    };
    const bayBkQ = query(
      collection(db, "bookings"),
      where("serviceId", "==", "bayliner"),
    );
    const bayExtQ = query(
      collection(db, "externalBookings"),
      where("serviceId", "==", "bayliner"),
    );
    const unsub4 = onSnapshot(bayBkQ, () => {
      if (!bkReady) {
        bkReady = true;
        return;
      }
      debouncedBayReload();
    });
    const unsub5 = onSnapshot(bayExtQ, () => {
      if (!extReady) {
        extReady = true;
        return;
      }
      debouncedBayReload();
    });
    return () => {
      unsub4();
      unsub5();
      clearTimeout(bayReloadTimer.current);
    };
  }, [loadBayCalendar]);

  const TABS = [
    { id: "calendar", label: "Calendar", Icon: FaCalendarAlt },
    { id: "speedboat-calendar", label: "SpeedBoat Tour", Icon: GiSpeedBoat },
    { id: "bayliner-calendar", label: "Durrës: Bayliner", Icon: FaAnchor },
    { id: "yacht-calendar", label: "Luxury Yacht", Icon: GiSailboat },
    {
      id: "secret-beach-calendar",
      label: "Secret Beach",
      Icon: FaUmbrellaBeach,
    },
    { id: "bookings", label: "All Bookings", Icon: FaListAlt },
    { id: "external", label: "External Bookings", Icon: FaGlobe },
    { id: "block", label: "Block Dates", Icon: FaBan },
    { id: "revenue", label: "Revenue", Icon: FaEuroSign },
  ];

  return (
    <div className="pt-20 pb-10 px-3 md:px-6 min-h-screen bg-slate-50">
      {/* Page header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl px-6 py-5 mb-6 flex flex-wrap items-center justify-between gap-3 shadow-md">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 bg-sky-500 rounded-xl flex items-center justify-center shadow-inner flex-shrink-0">
            <span className="text-white text-xl">🌊</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-white leading-tight">
              Admin Dashboard
            </h1>
            <p className="text-slate-400 text-xs mt-0.5">
              SeaPulse Albania · Booking Management
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            sessionStorage.removeItem("sp_admin");
            window.location.replace("/");
          }}
          className="flex items-center gap-2 text-sm text-slate-300 hover:text-white border border-slate-600 hover:border-slate-400 px-3 py-1.5 rounded-xl transition"
        >
          <FaSignOutAlt className="text-xs" />
          Sign out
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard
          label="Total Bookings"
          value={stats.total}
          icon={FaChartBar}
          iconBg="bg-slate-100"
          iconColor="text-slate-600"
        />
        <StatCard
          label="Pending"
          value={stats.pending}
          icon={FaClock}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
        />
        <StatCard
          label="Confirmed"
          value={stats.confirmed}
          icon={FaCheckCircle}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Completed"
          value={stats.completed}
          icon={FaFlag}
          iconBg="bg-emerald-100"
          iconColor="text-emerald-600"
        />
        <StatCard
          label="Cancelled"
          value={stats.cancelled}
          icon={FaTimesCircle}
          iconBg="bg-red-100"
          iconColor="text-red-500"
        />
        <StatCard
          label="Revenue"
          value={`€${stats.revenue.toLocaleString()}`}
          icon={FaEuroSign}
          iconBg="bg-teal-100"
          iconColor="text-teal-600"
        />
      </div>

      {/* Navigation tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-6 overflow-hidden">
        <div className="flex overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 whitespace-nowrap transition flex-shrink-0
                ${
                  activeTab === t.id
                    ? "border-sky-600 text-sky-700 bg-sky-50"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
            >
              <t.Icon className="text-xs" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-6 overflow-y-auto max-h-[75vh]">
        {/* ── CALENDAR TAB ── */}
        {activeTab === "calendar" && (
          <div>
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-2 text-xs mb-4">
              {[
                [
                  "border-l-emerald-400 bg-emerald-100 text-emerald-700",
                  "Open",
                ],
                [
                  "border-l-orange-400 bg-orange-100 text-orange-700",
                  "Almost full",
                ],
                [
                  "border-l-red-400    bg-red-100    text-red-700",
                  "Fully booked",
                ],
                ["border-l-gray-300   bg-gray-100   text-gray-500", "Blocked"],
              ].map(([cls, label]) => (
                <span
                  key={label}
                  className={`flex items-center gap-1.5 border-l-2 rounded-r-md pl-1.5 pr-2 py-0.5 font-medium text-[11px] ${cls}`}
                >
                  {label}
                </span>
              ))}
              <span className="hidden sm:inline ml-auto text-gray-400 text-[11px]">
                Tap any day to manage
              </span>
            </div>

            {/* Month navigator */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setCurrentDate((d) => subMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronLeft className="text-xs" />
              </button>
              <span className="flex-1 font-semibold text-slate-700 text-center text-sm sm:text-base">
                {currentDate.toLocaleDateString("en-GB", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={() => setCurrentDate((d) => addMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronRight className="text-xs" />
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl text-xs font-semibold hover:bg-sky-100 transition"
              >
                Today
              </button>
              <button
                onClick={loadCalendar}
                title="Refresh"
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition"
              >
                <FaSyncAlt className="text-xs" />
              </button>
            </div>

            {calLoading ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                Loading calendar…
              </div>
            ) : (
              <AdminMonthGrid
                year={currentDate.getFullYear()}
                month={currentDate.getMonth()}
                daySummaries={daySummaries}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
              />
            )}
          </div>
        )}

        {/* ── BOOKINGS TAB ── */}
        {activeTab === "bookings" && <AdminBookingsTable />}

        {/* ── EXTERNAL BOOKINGS TAB ── */}
        {activeTab === "external" && (
          <ExternalBookingsPanel
            onDataChange={loadCalendar}
            onNavigateToDate={(dateStr) => {
              const d = new Date(dateStr + "T12:00:00");
              setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1));
              setActiveTab("calendar");
            }}
          />
        )}

        {/* ── BLOCK DATES TAB ── */}
        {activeTab === "block" && (
          <BlockDatesSection onDataChange={loadCalendar} />
        )}

        {/* ── REVENUE TAB ── */}
        {activeTab === "revenue" && <RevenuePanel />}

        {/* ── DURRËS: SPEEDBOAT TOUR CALENDAR TAB ── */}
        {activeTab === "speedboat-calendar" && (
          <div>
            {/* Location badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-sky-100 text-sky-700 border border-sky-200 px-2.5 py-1 rounded-full">
                <GiSpeedBoat className="text-[10px]" /> Durrës · SpeedBoat Tour
              </span>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-2 text-xs mb-4">
              {[
                [
                  "border-l-emerald-400 bg-emerald-100 text-emerald-700",
                  "Open",
                ],
                [
                  "border-l-orange-400 bg-orange-100 text-orange-700",
                  "Almost full",
                ],
                [
                  "border-l-red-400    bg-red-100    text-red-700",
                  "Fully booked",
                ],
                ["border-l-gray-300   bg-gray-100   text-gray-500", "Blocked"],
              ].map(([cls, label]) => (
                <span
                  key={label}
                  className={`flex items-center gap-1.5 border-l-2 rounded-r-md pl-1.5 pr-2 py-0.5 font-medium text-[11px] ${cls}`}
                >
                  {label}
                </span>
              ))}
              <span className="hidden sm:inline ml-auto text-gray-400 text-[11px]">
                Tap any day to manage
              </span>
            </div>
            {/* Month navigator */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setSbCurrentDate((d) => subMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronLeft className="text-xs" />
              </button>
              <span className="flex-1 font-semibold text-slate-700 text-center text-sm sm:text-base">
                {sbCurrentDate.toLocaleDateString("en-GB", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={() => setSbCurrentDate((d) => addMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronRight className="text-xs" />
              </button>
              <button
                onClick={() => setSbCurrentDate(new Date())}
                className="px-3 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl text-xs font-semibold hover:bg-sky-100 transition"
              >
                Today
              </button>
              <button
                onClick={loadSbCalendar}
                title="Refresh"
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition"
              >
                <FaSyncAlt className="text-xs" />
              </button>
            </div>
            {sbCalLoading ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                Loading calendar…
              </div>
            ) : (
              <AdminMonthGrid
                year={sbCurrentDate.getFullYear()}
                month={sbCurrentDate.getMonth()}
                daySummaries={sbDaySummaries}
                selectedDay={sbSelectedDay}
                onSelectDay={setSbSelectedDay}
              />
            )}
          </div>
        )}

        {/* ── DURRËS: BAYLINER CALENDAR TAB ── */}
        {activeTab === "bayliner-calendar" && (
          <div>
            {/* Location badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-100 text-teal-700 border border-teal-200 px-2.5 py-1 rounded-full">
                <FaAnchor className="text-[10px]" /> Durrës · Bayliner Water
                Sports
              </span>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-2 text-xs mb-4">
              {[
                [
                  "border-l-emerald-400 bg-emerald-100 text-emerald-700",
                  "Open",
                ],
                [
                  "border-l-orange-400 bg-orange-100 text-orange-700",
                  "Almost full",
                ],
                [
                  "border-l-red-400    bg-red-100    text-red-700",
                  "Fully booked",
                ],
                ["border-l-gray-300   bg-gray-100   text-gray-500", "Blocked"],
              ].map(([cls, label]) => (
                <span
                  key={label}
                  className={`flex items-center gap-1.5 border-l-2 rounded-r-md pl-1.5 pr-2 py-0.5 font-medium text-[11px] ${cls}`}
                >
                  {label}
                </span>
              ))}
              <span className="hidden sm:inline ml-auto text-gray-400 text-[11px]">
                Tap any day to manage
              </span>
            </div>
            {/* Month navigator */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setBayCurrentDate((d) => subMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronLeft className="text-xs" />
              </button>
              <span className="flex-1 font-semibold text-slate-700 text-center text-sm sm:text-base">
                {bayCurrentDate.toLocaleDateString("en-GB", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={() => setBayCurrentDate((d) => addMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronRight className="text-xs" />
              </button>
              <button
                onClick={() => setBayCurrentDate(new Date())}
                className="px-3 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl text-xs font-semibold hover:bg-sky-100 transition"
              >
                Today
              </button>
              <button
                onClick={loadBayCalendar}
                title="Refresh"
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition"
              >
                <FaSyncAlt className="text-xs" />
              </button>
            </div>
            {bayCalLoading ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                Loading calendar…
              </div>
            ) : (
              <AdminMonthGrid
                year={bayCurrentDate.getFullYear()}
                month={bayCurrentDate.getMonth()}
                daySummaries={bayDaySummaries}
                selectedDay={baySelectedDay}
                onSelectDay={setBaySelectedDay}
              />
            )}
          </div>
        )}

        {/* ── LUXURY YACHT CALENDAR TAB ── */}
        {activeTab === "yacht-calendar" && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-violet-100 text-violet-700 border border-violet-200 px-2.5 py-1 rounded-full">
                <GiSailboat className="text-[10px]" /> Luxury Yacht Charter
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs mb-4">
              {[
                [
                  "border-l-emerald-400 bg-emerald-100 text-emerald-700",
                  "Open",
                ],
                [
                  "border-l-orange-400 bg-orange-100 text-orange-700",
                  "Almost full",
                ],
                [
                  "border-l-red-400    bg-red-100    text-red-700",
                  "Fully booked",
                ],
                ["border-l-gray-300   bg-gray-100   text-gray-500", "Blocked"],
              ].map(([cls, label]) => (
                <span
                  key={label}
                  className={`flex items-center gap-1.5 border-l-2 rounded-r-md pl-1.5 pr-2 py-0.5 font-medium text-[11px] ${cls}`}
                >
                  {label}
                </span>
              ))}
              <span className="hidden sm:inline ml-auto text-gray-400 text-[11px]">
                Tap any day to manage
              </span>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setYachtCurrentDate((d) => subMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronLeft className="text-xs" />
              </button>
              <span className="flex-1 font-semibold text-slate-700 text-center text-sm sm:text-base">
                {yachtCurrentDate.toLocaleDateString("en-GB", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={() => setYachtCurrentDate((d) => addMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronRight className="text-xs" />
              </button>
              <button
                onClick={() => setYachtCurrentDate(new Date())}
                className="px-3 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl text-xs font-semibold hover:bg-sky-100 transition"
              >
                Today
              </button>
              <button
                onClick={loadYachtCalendar}
                title="Refresh"
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition"
              >
                <FaSyncAlt className="text-xs" />
              </button>
            </div>
            {yachtCalLoading ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                Loading calendar…
              </div>
            ) : (
              <AdminMonthGrid
                year={yachtCurrentDate.getFullYear()}
                month={yachtCurrentDate.getMonth()}
                daySummaries={yachtDaySummaries}
                selectedDay={yachtSelectedDay}
                onSelectDay={setYachtSelectedDay}
              />
            )}
          </div>
        )}

        {/* ── SECRET BEACH ESCAPE CALENDAR TAB ── */}
        {activeTab === "secret-beach-calendar" && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-full">
                <FaUmbrellaBeach className="text-[10px]" /> Secret Beach Escape
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs mb-4">
              {[
                [
                  "border-l-emerald-400 bg-emerald-100 text-emerald-700",
                  "Open",
                ],
                [
                  "border-l-orange-400 bg-orange-100 text-orange-700",
                  "Almost full",
                ],
                [
                  "border-l-red-400    bg-red-100    text-red-700",
                  "Fully booked",
                ],
                ["border-l-gray-300   bg-gray-100   text-gray-500", "Blocked"],
              ].map(([cls, label]) => (
                <span
                  key={label}
                  className={`flex items-center gap-1.5 border-l-2 rounded-r-md pl-1.5 pr-2 py-0.5 font-medium text-[11px] ${cls}`}
                >
                  {label}
                </span>
              ))}
              <span className="hidden sm:inline ml-auto text-gray-400 text-[11px]">
                Tap any day to manage
              </span>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setSbeCurrentDate((d) => subMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronLeft className="text-xs" />
              </button>
              <span className="flex-1 font-semibold text-slate-700 text-center text-sm sm:text-base">
                {sbeCurrentDate.toLocaleDateString("en-GB", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={() => setSbeCurrentDate((d) => addMonths(d, 1))}
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition"
              >
                <FaChevronRight className="text-xs" />
              </button>
              <button
                onClick={() => setSbeCurrentDate(new Date())}
                className="px-3 py-1.5 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl text-xs font-semibold hover:bg-sky-100 transition"
              >
                Today
              </button>
              <button
                onClick={loadSbeCalendar}
                title="Refresh"
                className="w-9 h-9 flex items-center justify-center border border-slate-200 rounded-xl text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition"
              >
                <FaSyncAlt className="text-xs" />
              </button>
            </div>
            {sbeCalLoading ? (
              <div className="flex items-center justify-center h-64 text-gray-400">
                Loading calendar…
              </div>
            ) : (
              <AdminMonthGrid
                year={sbeCurrentDate.getFullYear()}
                month={sbeCurrentDate.getMonth()}
                daySummaries={sbeDaySummaries}
                selectedDay={sbeSelectedDay}
                onSelectDay={setSbeSelectedDay}
              />
            )}
          </div>
        )}
      </div>

      {/* Main services day detail modal */}
      {selectedDay && (
        <DayDetailPanel
          date={selectedDay}
          onClose={() => {
            setSelectedDay(null);
            loadCalendar();
          }}
          onDataChange={loadCalendar}
        />
      )}

      {/* Durrës: Bayliner day detail modal */}
      {baySelectedDay && (
        <DayDetailPanel
          date={baySelectedDay}
          defaultService="bayliner"
          onClose={() => {
            setBaySelectedDay(null);
            loadBayCalendar();
          }}
          onDataChange={loadBayCalendar}
        />
      )}

      {/* Luxury Yacht day detail modal */}
      {yachtSelectedDay && (
        <DayDetailPanel
          date={yachtSelectedDay}
          defaultService="luxury-yacht"
          onClose={() => {
            setYachtSelectedDay(null);
            loadYachtCalendar();
          }}
          onDataChange={loadYachtCalendar}
        />
      )}

      {/* Secret Beach Escape day detail modal */}
      {sbeSelectedDay && (
        <DayDetailPanel
          date={sbeSelectedDay}
          defaultService="secret-beach-escape"
          onClose={() => {
            setSbeSelectedDay(null);
            loadSbeCalendar();
          }}
          onDataChange={loadSbeCalendar}
        />
      )}

      {/* Durrës: SpeedBoat Tour day detail modal */}
      {sbSelectedDay && (
        <DayDetailPanel
          date={sbSelectedDay}
          defaultService="speedboat"
          onClose={() => {
            setSbSelectedDay(null);
            loadSbCalendar();
          }}
          onDataChange={loadSbCalendar}
        />
      )}
    </div>
  );
}
